import React, { useCallback, useEffect, useState } from "react";
import {
  CheckCircle2, Hammer, Loader2, Mail, Pencil, Printer, ReceiptIndianRupee,
  ShieldCheck, SlidersHorizontal, XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fdate, fdatetime, money } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Field } from "@/pages/crm/LeadForm";
import { REPAIR_TONE } from "./Repairs";

const FIELD_LABELS = {
  serial_no: "Serial number", inspection: "Inspection findings", diagnosis: "Diagnosis",
  parts: "Parts table", labour: "Service and labour", other_costs: "Other charges",
  advance: "Advance payable", warranty: "Repair warranty", expected_delivery: "Expected delivery",
  terms: "Terms and conditions", notes: "Notes", bank: "Bank details",
};

const Row = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 border-b border-border/60 py-2 last:border-0">
    <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
    <span className="max-w-[62%] text-right text-sm font-medium">{value || "\u2014"}</span>
  </div>
);

export const RepairDetail = ({ jobId, open, meta, onOpenChange, onEdit, onChanged }) => {
  const { can, isAdmin } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [mail, setMail] = useState(null);
  const [decide, setDecide] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [fieldsOpen, setFieldsOpen] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!jobId) return;
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get(`/repairs/${jobId}`);
      setJob(data);
    } catch (e) { toast.error(errMsg(e)); onOpenChange(false); } finally { setLoading(false); }
  }, [jobId]); // eslint-disable-line

  useEffect(() => { if (open) load(); }, [open, jobId]); // eslint-disable-line

  const editable = can("repairs", "edit");
  const pf = job?.print_fields || {};

  const setStatus = async (status, remarks = "") => {
    setBusy(true);
    try {
      await api.post(`/repairs/${job.id}/status`, { status, remarks });
      toast.success(`Marked ${status}`);
      setDecide(null);
      setRemarks("");
      await load(true);
      onChanged?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const openMail = async () => {
    try {
      const { data } = await api.get(`/repairs/${job.id}/email-preview`);
      setMail(data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const sendMail = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/repairs/${job.id}/email`, {
        to: (mail.to || []).filter(Boolean), cc: mail.cc || [],
        subject: mail.subject, body: mail.body,
      });
      toast.success(data?.status === "outbox"
        ? "Saved to the Email Outbox until SMTP is configured"
        : "Estimate emailed to the customer");
      setMail(null);
      await load(true);
      onChanged?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const raiseInvoice = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/repairs/${job.id}/invoice`);
      toast.success(data.existing ? `Invoice ${data.invoice.number} already exists`
        : `Invoice ${data.invoice.number} created`);
      await load(true);
      onChanged?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const saveFields = async (next) => {
    setJob((j) => ({ ...j, print_fields: next }));
    try {
      await api.post(`/repairs/${job.id}/print-fields`, { print_fields: next });
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto scroll-slim p-0 sm:max-w-3xl">
        <SheetTitle className="sr-only">{job?.number || "Repair estimate"}</SheetTitle>
        <SheetDescription className="sr-only">Repair estimate, approval, service progress and invoice.</SheetDescription>

        {loading || !job ? (
          <div className="grid h-64 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <div className="flex min-h-full flex-col">
            <div className="sticky top-0 z-40 flex flex-wrap items-center gap-2 border-b border-border bg-background/95 px-5 py-3 backdrop-blur print:hidden">
              <div className="mr-auto">
                <p className="num font-display text-lg font-bold">
                  {job.number}{job.rev ? ` Rev ${job.rev}` : ""}
                </p>
                <p className="text-xs text-muted-foreground">{job.equipment || "Repair"} &middot; {fdate(job.date)}</p>
              </div>
              <StatusBadge status={job.status} tone={REPAIR_TONE[job.status]} />
              {editable && (
                <Button size="sm" variant="outline" data-testid="rd-edit" onClick={() => onEdit?.(job)}>
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </Button>
              )}
              {editable && (
                <Button size="sm" variant="outline" data-testid="rd-fields" onClick={() => setFieldsOpen(true)}>
                  <SlidersHorizontal className="h-3.5 w-3.5" /> Printed fields
                </Button>
              )}
              {can("repairs", "email") && (
                <Button size="sm" variant="outline" data-testid="rd-email" onClick={openMail}>
                  <Mail className="h-3.5 w-3.5" /> Email
                </Button>
              )}
              <Button size="sm" variant="outline" data-testid="rd-print" onClick={() => window.print()}>
                <Printer className="h-3.5 w-3.5" /> Print / PDF
              </Button>
              {editable && ["Sent", "Draft"].includes(job.status) && (
                <>
                  <Button size="sm" variant="success" data-testid="rd-approve" onClick={() => setDecide("Approved")}>
                    <CheckCircle2 className="h-3.5 w-3.5" /> Customer approved
                  </Button>
                  <Button size="sm" variant="outline" className="text-destructive" data-testid="rd-reject"
                    onClick={() => setDecide("Rejected")}>
                    <XCircle className="h-3.5 w-3.5" /> Rejected
                  </Button>
                </>
              )}
              {editable && job.status === "Approved" && (
                <Button size="sm" data-testid="rd-start" onClick={() => setStatus("Repair in Progress")}>
                  <Hammer className="h-3.5 w-3.5" /> Start repair
                </Button>
              )}
              {editable && job.status === "Repair in Progress" && (
                <Button size="sm" variant="success" data-testid="rd-complete" onClick={() => setDecide("Completed")}>
                  <CheckCircle2 className="h-3.5 w-3.5" /> Mark completed
                </Button>
              )}
              {can("invoices", "add") && ["Completed", "Invoiced"].includes(job.status) && (
                <Button size="sm" variant="soft" data-testid="rd-invoice" disabled={busy} onClick={raiseInvoice}>
                  <ReceiptIndianRupee className="h-3.5 w-3.5" />
                  {job.invoice_number ? `Invoice ${job.invoice_number}` : "Raise invoice"}
                </Button>
              )}
              <SheetClose asChild>
                <Button size="sm" variant="ghost" data-testid="rd-close">Close</Button>
              </SheetClose>
            </div>

            <Tabs defaultValue="estimate">
              <div className="scroll-slim overflow-x-auto border-b border-border px-6 print:hidden">
                <TabsList className="h-11 w-max bg-transparent p-0">
                  <TabsTrigger value="estimate" data-testid="rd-tab-estimate">Estimate</TabsTrigger>
                  <TabsTrigger value="job" data-testid="rd-tab-job">Service job</TabsTrigger>
                  <TabsTrigger value="revisions" data-testid="rd-tab-revisions">
                    Revisions{(job.revisions || []).length ? ` (${job.revisions.length})` : ""}
                  </TabsTrigger>
                  <TabsTrigger value="timeline" data-testid="rd-tab-timeline">Timeline</TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="estimate" className="m-0">
                <div id="print-area" className="bg-card px-6 py-7 sm:px-10">
                  <div className="flex flex-wrap items-start justify-between gap-6 border-b border-border pb-6">
                    <div className="flex items-start gap-3">
                      <span className="grid h-11 w-11 place-items-center rounded-lg bg-gradient-warm text-primary-foreground">
                        <ShieldCheck className="h-5 w-5" />
                      </span>
                      <div>
                        <p className="font-display text-lg font-bold">{job.org?.name || "Megatronix Vision"}</p>
                        <p className="max-w-xs text-xs leading-relaxed text-muted-foreground">{job.org?.address}</p>
                        <p className="text-xs text-muted-foreground">{job.org?.phone} {job.org?.email}</p>
                        {job.org?.gstin && <p className="num text-xs text-muted-foreground">GSTIN: {job.org.gstin}</p>}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-display text-xl font-bold uppercase tracking-wide text-primary">Repair Estimate</p>
                      <p className="num text-sm font-semibold">{job.number}{job.rev ? ` Rev ${job.rev}` : ""}</p>
                      <p className="text-xs text-muted-foreground">Date: {fdate(job.date)}</p>
                      <p className="text-xs text-muted-foreground">Valid until: {fdate(job.valid_until)}</p>
                      <div className="mt-2 flex justify-end"><StatusBadge status={job.status} tone={REPAIR_TONE[job.status]} /></div>
                    </div>
                  </div>

                  <div className="grid gap-6 py-6 sm:grid-cols-2">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Customer</p>
                      <p className="mt-1 text-sm font-semibold">{job.customer_name}</p>
                      {job.company && <p className="text-sm">{job.company}</p>}
                      {job.address && <p className="text-xs text-muted-foreground">{job.address}</p>}
                      {job.phone && <p className="text-xs text-muted-foreground">{job.phone}</p>}
                      {job.email && <p className="text-xs text-muted-foreground">{job.email}</p>}
                    </div>
                    <div className="sm:text-right">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Equipment</p>
                      <p className="mt-1 text-sm font-semibold">{job.equipment}</p>
                      {job.model_no && <p className="num text-xs text-muted-foreground">Model {job.model_no}</p>}
                      {pf.serial_no !== false && job.serial_no && <p className="num text-xs text-muted-foreground">Serial {job.serial_no}</p>}
                      {job.accessories && <p className="text-xs text-muted-foreground">With: {job.accessories}</p>}
                      {job.under_warranty && <p className="text-xs font-semibold text-success">Under warranty</p>}
                    </div>
                  </div>

                  <div className="space-y-3 border-t border-border py-5">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Complaint</p>
                      <p className="text-sm">{job.complaint}</p>
                    </div>
                    {pf.inspection !== false && job.inspection && (
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Inspection findings</p>
                        <p className="text-sm">{job.inspection}</p>
                      </div>
                    )}
                    {pf.diagnosis !== false && job.diagnosis && (
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Diagnosis</p>
                        <p className="text-sm">{job.diagnosis}</p>
                      </div>
                    )}
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[520px] text-sm">
                      <thead>
                        <tr className="border-y border-border bg-muted/50">
                          <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Description</th>
                          <th className="px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Qty / hr</th>
                          <th className="px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Rate</th>
                          <th className="px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">GST</th>
                          <th className="px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pf.parts !== false && (job.parts || []).map((p, i) => (
                          <tr key={`p${i}`} className="border-b border-border/70">
                            <td className="px-3 py-2.5"><b>{p.name}</b>{p.remarks ? <span className="block text-xs text-muted-foreground">{p.remarks}</span> : null}</td>
                            <td className="num px-3 py-2.5 text-right">{p.qty}</td>
                            <td className="num px-3 py-2.5 text-right">{money(p.price)}</td>
                            <td className="num px-3 py-2.5 text-right">{p.tax_rate}%</td>
                            <td className="num px-3 py-2.5 text-right font-semibold">{money(p.qty * p.price)}</td>
                          </tr>
                        ))}
                        {pf.labour !== false && (job.labour || []).map((l, i) => (
                          <tr key={`l${i}`} className="border-b border-border/70">
                            <td className="px-3 py-2.5">{l.description}</td>
                            <td className="num px-3 py-2.5 text-right">{l.hours}</td>
                            <td className="num px-3 py-2.5 text-right">{money(l.rate)}</td>
                            <td className="num px-3 py-2.5 text-right">{l.tax_rate}%</td>
                            <td className="num px-3 py-2.5 text-right font-semibold">{money(l.hours * l.rate)}</td>
                          </tr>
                        ))}
                        {pf.other_costs !== false && (job.other_costs || []).map((o, i) => (
                          <tr key={`o${i}`} className="border-b border-border/70">
                            <td className="px-3 py-2.5">{o.description}</td>
                            <td className="num px-3 py-2.5 text-right">1</td>
                            <td className="num px-3 py-2.5 text-right">{money(o.amount)}</td>
                            <td className="num px-3 py-2.5 text-right">{o.tax_rate}%</td>
                            <td className="num px-3 py-2.5 text-right font-semibold">{money(o.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-5 flex justify-end">
                    <div className="w-full max-w-xs space-y-1.5">
                      <div className="flex justify-between text-sm"><span className="text-muted-foreground">Subtotal</span><span className="num">{money(job.subtotal)}</span></div>
                      <div className="flex justify-between text-sm"><span className="text-muted-foreground">GST</span><span className="num">{money(job.tax_total)}</span></div>
                      {Number(job.discount) > 0 && (
                        <div className="flex justify-between text-sm"><span className="text-muted-foreground">Discount</span><span className="num text-destructive">- {money(job.discount)}</span></div>
                      )}
                      <div className="flex items-center justify-between border-t border-border pt-2">
                        <span className="text-sm font-semibold">Total estimate</span>
                        <span className="num text-lg font-bold text-primary" data-testid="rd-total">{money(job.total)}</span>
                      </div>
                      {pf.advance !== false && Number(job.advance_amount) > 0 && (
                        <>
                          <div className="flex justify-between text-sm"><span className="text-muted-foreground">Advance payable</span><span className="num text-warning">{money(job.advance_amount)}</span></div>
                          <div className="flex justify-between text-sm"><span className="text-muted-foreground">Balance on delivery</span><span className="num">{money(job.balance_due)}</span></div>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="mt-6 grid gap-4 text-xs sm:grid-cols-2">
                    {pf.expected_delivery !== false && job.expected_delivery && (
                      <p><span className="text-muted-foreground">Expected delivery: </span><b>{fdate(job.expected_delivery)}</b></p>
                    )}
                    {pf.warranty !== false && (
                      <p><span className="text-muted-foreground">Repair warranty: </span><b>{job.warranty_months} month(s)</b></p>
                    )}
                  </div>

                  {pf.notes !== false && job.notes && (
                    <div className="mt-5">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Notes</p>
                      <p className="mt-1 whitespace-pre-line text-sm">{job.notes}</p>
                    </div>
                  )}
                  {pf.terms !== false && job.terms && (
                    <div className="mt-5">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Terms and conditions</p>
                      <p className="mt-1 whitespace-pre-line text-xs text-muted-foreground">{job.terms}</p>
                    </div>
                  )}
                  {pf.bank !== false && job.bank?.bank_name && (
                    <div className="mt-5 rounded-lg border border-border bg-muted/40 p-4">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Bank details</p>
                      <div className="mt-1 grid gap-x-6 text-xs sm:grid-cols-2">
                        <p>{job.bank.account_name}</p>
                        <p className="num">A/C {job.bank.account_number}</p>
                        <p>{job.bank.bank_name}, {job.bank.branch}</p>
                        <p className="num">IFSC {job.bank.ifsc}</p>
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="job" className="m-0 px-6 py-5">
                <Row label="Engineer" value={job.engineer_name} />
                <Row label="Approval" value={job.approval
                  ? `${job.approval.decision} by ${job.approval.by} (${job.approval.mode}) on ${fdatetime(job.approval.at)}`
                  : "not recorded yet"} />
                {job.approval?.remarks && <Row label="Customer remarks" value={job.approval.remarks} />}
                <Row label="Repair started" value={job.repair_started_at ? fdatetime(job.repair_started_at) : ""} />
                <Row label="Repair completed" value={job.repair_completed_at ? fdatetime(job.repair_completed_at) : ""} />
                <Row label="Work done" value={job.work_done} />
                <Row label="Invoice" value={job.invoice_number} />
                <Row label="Created by" value={`${job.created_by_name} \u00b7 ${fdatetime(job.created_at)}`} />
                <p className="mt-4 rounded-lg border border-dashed border-border px-3 py-2 text-xs text-muted-foreground">
                  The customer can approve or reject this estimate from the link in the email; whatever they
                  choose is recorded here automatically.
                </p>
              </TabsContent>

              <TabsContent value="revisions" className="m-0 space-y-3 px-6 py-5" data-testid="rd-revisions">
                {(job.revisions || []).length === 0 && (
                  <p className="rounded-lg border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
                    No revisions yet. Any change after the estimate is sent is kept here.
                  </p>
                )}
                {(job.revisions || []).slice().reverse().map((r, i) => (
                  <div key={i} className="rounded-xl border border-border p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-sm font-semibold">Rev {r.rev} &middot; {money(r.total)}</p>
                      <p className="text-xs text-muted-foreground">{r.by} &middot; {fdatetime(r.at)}</p>
                    </div>
                    <p className="text-xs text-muted-foreground">{r.reason}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {(r.parts || []).length} part(s), {(r.labour || []).length} service line(s), status was {r.status}
                    </p>
                  </div>
                ))}
              </TabsContent>

              <TabsContent value="timeline" className="m-0 px-6 py-5">
                <div className="space-y-3">
                  {(job.history || []).slice().reverse().map((h, i) => (
                    <div key={i} className="relative pl-5">
                      <span className="absolute left-0 top-1.5 h-2 w-2 rounded-full bg-primary" />
                      <p className="text-sm font-semibold">{h.action}</p>
                      {h.note && <p className="text-xs text-muted-foreground">{h.note}</p>}
                      <p className="text-[11px] text-muted-foreground">{h.by} &middot; {fdatetime(h.at)}</p>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        )}

        {/* decision / completion */}
        <Dialog open={!!decide} onOpenChange={(v) => !v && setDecide(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">
                {decide === "Completed" ? "Close the repair" : `Record ${decide?.toLowerCase()}`}
              </DialogTitle>
              <DialogDescription>
                {decide === "Completed"
                  ? "Write what was actually done - it goes on the job record and the invoice notes."
                  : "Use this when the customer tells you over phone or in person."}
              </DialogDescription>
            </DialogHeader>
            <Field label={decide === "Completed" ? "Work done" : "Remarks"}>
              <Textarea rows={3} value={remarks} data-testid="rd-decision-remarks"
                onChange={(e) => setRemarks(e.target.value)} />
            </Field>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDecide(null)} data-testid="rd-decision-cancel">Cancel</Button>
              <Button disabled={busy} data-testid="rd-decision-save" onClick={() => setStatus(decide, remarks)}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* printed fields */}
        <Dialog open={fieldsOpen} onOpenChange={setFieldsOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">What appears on the estimate</DialogTitle>
              <DialogDescription>
                Untick anything you do not want on the printed copy or in the customer email.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-2 sm:grid-cols-2" data-testid="rd-field-toggles">
              {Object.entries(FIELD_LABELS).map(([k, label]) => (
                <label key={k} className="flex cursor-pointer items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm">
                  <Checkbox checked={pf[k] !== false} data-testid={`rd-field-${k}`}
                    onCheckedChange={(v) => saveFields({ ...pf, [k]: !!v })} />
                  {label}
                </label>
              ))}
            </div>
            <DialogFooter>
              <Button onClick={() => setFieldsOpen(false)} data-testid="rd-fields-done">Done</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* email */}
        <Dialog open={!!mail} onOpenChange={(v) => !v && setMail(null)}>
          <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto scroll-slim">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">Email this estimate</DialogTitle>
              <DialogDescription>
                The customer gets the estimate with an approve / reject link. {isAdmin ? "" : ""}
              </DialogDescription>
            </DialogHeader>
            {mail && (
              <div className="space-y-3">
                <Field label="To" required>
                  <Input value={(mail.to || []).join(", ")} data-testid="rd-mail-to"
                    onChange={(e) => setMail({ ...mail, to: e.target.value.split(",").map((s) => s.trim()) })} />
                </Field>
                <Field label="CC">
                  <Input value={(mail.cc || []).join(", ")} data-testid="rd-mail-cc"
                    onChange={(e) => setMail({ ...mail, cc: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} />
                </Field>
                <Field label="Subject" required>
                  <Input value={mail.subject} data-testid="rd-mail-subject"
                    onChange={(e) => setMail({ ...mail, subject: e.target.value })} />
                </Field>
                <Field label="Message" hint="Formatted estimate - edit the HTML only if you need to.">
                  <Textarea rows={8} value={mail.body} className="font-mono text-xs" data-testid="rd-mail-body"
                    onChange={(e) => setMail({ ...mail, body: e.target.value })} />
                </Field>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setMail(null)} data-testid="rd-mail-cancel">Cancel</Button>
              <Button onClick={sendMail} disabled={busy} data-testid="rd-mail-send">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Send
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </SheetContent>
    </Sheet>
  );
};
